import {
  required,
  SelectInput,
  ReferenceInput,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  NullableBooleanInput,
  useSimpleFormIteratorItem,
  BooleanInput,
  TextInput,
  FormDataConsumer,
  useDataProvider,
  AutocompleteInput,
} from "react-admin";
import { Grid, TextField, InputLabel, Select, MenuItem, FormControl, FormHelperText } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { AdminExamsPaymentMethodChoices, LanguageOptions } from "utils/choices";
import { settings } from "app";
import { useEffect, useState } from "react";
import { AdminExamsPaymentMethod, CitizenshipIds } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import { administrationRequests } from "api/administrationApi";
import { useNotifyError } from "utils/useNotifyError";

const paymentValidation = (value, allValues) => {
  const noPayment = AdminExamsPaymentMethod.NoPayment;

  if (value === noPayment && allValues.isPaidDocumentProcessing === noPayment)
    return "Трябва поне една от таксите да е платена.";
};

const CandidacyInfo = ({
  isFreeExams,
  isFreeDocument,
  formData,
  isForeigner,
  campaignId,
  handleSelectedSpecialityIds,
  selectedSpecialityIds,
  availableExams,
  setSelectedSpecialityIds,
  setSchoolSubjectGrades,
  components,
  setComponents,
}) => {
  const dataProvider = useDataProvider();
  const notifyError = useNotifyError();
  const { setValue } = useFormContext();
  const isRestoring = useWatch({ name: "reinstatesRights" });

  const regular = useWatch({ name: "regular" });
  const isFromAnotherUniversity = useWatch({ name: "isFromAnotherUniversity" });
  const usingPreparatoryClass = useWatch({ name: "usingPreparatoryClass" });
  const participatesInRanking = useWatch({ name: "participatesInRanking" });

  useEffect(() => {
    if (regular === true) {
      setValue("reinstatesRights", false);
      setValue("isFromAnotherUniversity", false);
    }
  }, [regular, isFromAnotherUniversity, isRestoring, setValue]);

  useEffect(() => setValue("isPaidExams", null), [usingPreparatoryClass, setValue]);
  useEffect(() => setValue("participatesInRanking", !usingPreparatoryClass), [usingPreparatoryClass, setValue]);
  useEffect(() => setValue("usingPreparatoryClass", !participatesInRanking), [participatesInRanking, setValue]);

  useEffect(() => {
    if (isForeigner) return;
    if (isFreeExams) {
      setValue("isPaidExams", AdminExamsPaymentMethod.NoPayment);
    } else {
      setValue("isPaidExams", null);
    }
    if (isFreeDocument) {
      setValue("isPaidDocumentProcessing", AdminExamsPaymentMethod.NoPayment);
    } else {
      setValue("isPaidDocumentProcessing", null);
    }
  }, [isFreeExams, isFreeDocument, isForeigner, setValue]);

  const [error, setError] = useState(false);
  const [scoreLineOptions, setScoreLineOptions] = useState([]);
  const [helperText, setHelperText] = useState("");
  const [touchedComponentIds, setTouchedComponentIds] = useState(new Set());

  const validate = val => {
    const regex = /^\d+(\.\d{1,2})?$/;
    const num = parseFloat(val);

    if (!regex.test(val)) {
      setError(true);
      setHelperText("Моля, въведете число с до два знака след десетичната запетая.");
    } else if (isNaN(num) || num < 2 || num > 6) {
      setError(true);
      setHelperText("Оценката трябва да е между 2 и 6.");
    } else {
      setError(false);
      setHelperText("");
    }
  };
  const handleChangeScoreLineComponents = ({ specialityId, lineId, componentId }) => {
    if (selectedSpecialityIds.includes(specialityId)) {
      setComponents(prev => {
        const existing = prev.findIndex(x => x.specialityId === specialityId);
        const newScoreLine = { specialityId, lineId, componentId: componentId };

        if (existing !== -1) {
          const updated = [...prev];
          updated[existing] = newScoreLine;
          return updated;
        } else {
          return [...prev, newScoreLine];
        }
      });
    } else {
      setComponents(components?.filter(item => item.specialityId !== specialityId));
    }
  };

  useEffect(() => {
    if (isRestoring) {
      setValue("specialities", [{}]);
      setSelectedSpecialityIds([]);
      setValue("admissionAfterEducationLevelId", null);
    }
  }, [isRestoring, setValue, setSelectedSpecialityIds]);

  useEffect(() => {
    if (!isForeigner && selectedSpecialityIds?.length > 0) {
      const fetchScoreLineData = async () => {
        try {
          const data = await Promise.all(
            selectedSpecialityIds.map(async specialityId => {
              if (!specialityId) return null;

              const res = await dataProvider.customRequest(
                candidateRequests.getSpecialityScoreFormulasManualSelectionSelect(specialityId, {
                  citizenshipId: CitizenshipIds.Bulgarians,
                }),
              );
              return {
                specialityId,
                lineId: res.data[0]?.lineId,
                options: res.data[0]?.components || [],
              };
            }),
          );

          const filteredResults = data.filter(Boolean);
          setScoreLineOptions(filteredResults);
        } catch (error) {
          notifyError(error);
        }
      };

      fetchScoreLineData();
    }
  }, [selectedSpecialityIds, isForeigner, dataProvider, notifyError]);

  useEffect(() => {
    const fetchStudyType = async () => {
      try {
        const res = await dataProvider.customRequest(
          administrationRequests.getStudyTypesBySpecialityId(selectedSpecialityIds[0]),
        );
        if (res?.data?.length === 1) setValue("specialities[0].studyTypeId", res?.data[0]?.id);
      } catch (error) {
        console.error("Неуспешно зареждане на типа образование", error);
      }
    };

    if (isForeigner && selectedSpecialityIds?.length > 0) {
      fetchStudyType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialityIds]);

  const IndexField = () => {
    const { index } = useSimpleFormIteratorItem();
    return (
      <NumberInput
        disabled
        sx={{ display: "none" }}
        source="priority"
        label="Приоритет"
        defaultValue={index + 1}
      />
    );
  };

  return (
    <Grid
      container
      spacing={4}
      sx={settings.style.gridContainer}
    >
      <>
        <Grid
          container
          spacing={2}
        >
          {isForeigner ? (
            <Grid
              item
              xs={12}
              ml={4}
            >
              <ArrayInput
                source="specialities"
                label=" "
                defaultValue={[{}]}
                fullWidth
              >
                <SimpleFormIterator
                  inline
                  disableReordering
                  disableAdd
                  disableRemove
                  sx={settings.style.iteratorNone}
                  fullWidth
                >
                  <FormDataConsumer>
                    {({ scopedFormData }) => {
                      return (
                        <Grid
                          container
                          spacing={4}
                        >
                          <Grid
                            item
                            xs={12}
                            md={4}
                          >
                            <ReferenceInput
                              source="specialityId"
                              reference={candidateRequests.getCampaignSpecialitiesSelect(campaignId)}
                              fullWidth
                            >
                              <SelectInput
                                onChange={e => handleSelectedSpecialityIds(formData?.specialities)}
                                label="Специалност"
                                optionText="nameBg"
                                validate={[required()]}
                              />
                            </ReferenceInput>
                          </Grid>
                          <NumberInput
                            sx={{ display: "none" }}
                            source="priority"
                            label="Приоритет"
                            defaultValue={1}
                          />
                          {scopedFormData?.specialityId && (
                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              <>
                                <ReferenceInput
                                  source="studyTypeId"
                                  reference={administrationRequests.getStudyTypesBySpecialityId(
                                    scopedFormData.specialityId,
                                  )}
                                  fullWidth
                                >
                                  <SelectInput
                                    label="Тип образование"
                                    optionText="nameBg"
                                    optionValue="id"
                                  />
                                </ReferenceInput>
                              </>
                            </Grid>
                          )}
                        </Grid>
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
          ) : (
            <>
              <Grid
                item
                xs={12}
              >
                <ArrayInput
                  source="specialities"
                  label=" "
                  defaultValue={[{}]}
                >
                  <SimpleFormIterator
                    getItemLabel={index => (
                      <div
                        style={{
                          borderRadius: "20px",
                          border: "1px solid black",
                          width: "min-content",
                          height: "min-content",
                          background: "#5a0896",
                          padding: ".5rem",
                          paddingLeft: ".8rem",
                          paddingRight: ".8rem",
                          color: "#fff",
                          marginTop: ".5rem",
                          marginRight: "1rem",
                        }}
                      >
                        {index + 1}
                      </div>
                    )}
                    inline
                    disableReordering={isRestoring === true}
                    disableAdd={isRestoring === true}
                    disableRemove={isRestoring === true}
                    disableClear
                    sx={settings.style.iteratorNone}
                  >
                    <FormDataConsumer>
                      {({ scopedFormData }) => {
                        return (
                          <Grid
                            container
                            spacing={2}
                          >
                            <Grid
                              item
                              xs={12}
                              lg={4}
                            >
                              <ReferenceInput
                                source="specialityId"
                                reference={candidateRequests.getCampaignSpecialitiesSelect(campaignId)}
                                filter={scopedFormData?.specialityId ? { excludeIds: selectedSpecialityIds } : {}}
                              >
                                <SelectInput
                                  onChange={e => handleSelectedSpecialityIds(formData?.specialities)}
                                  label="Специалност"
                                  optionText="nameBg"
                                  validate={[required()]}
                                  resettable
                                />
                                <IndexField />
                              </ReferenceInput>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              lg={4}
                            >
                              {scopedFormData?.specialityId && (
                                <>
                                  <ReferenceInput
                                    source="studyTypeId"
                                    reference={administrationRequests.getStudyTypesBySpecialityId(
                                      scopedFormData.specialityId,
                                    )}
                                  >
                                    <SelectInput
                                      label="Тип образование"
                                      optionText="nameBg"
                                    />
                                  </ReferenceInput>
                                </>
                              )}
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              lg={4}
                            >
                              {scoreLineOptions?.map((item, idx) => {
                                return (
                                  item.specialityId === scopedFormData?.specialityId &&
                                  item?.options.length > 0 && (
                                    <FormControl
                                      key={idx}
                                      sx={{ width: "20rem" }}
                                      required={item.options?.length > 0}
                                      error={
                                        touchedComponentIds.has(item.specialityId) &&
                                        !components?.find(x => x.specialityId === item?.specialityId)?.componentId
                                      }
                                    >
                                      <InputLabel id="componentId">Балообразуващ компонент</InputLabel>
                                      <Select
                                        labelId="componentId"
                                        id="componentId"
                                        value={
                                          components?.find(x => x.specialityId === item?.specialityId)?.componentId ||
                                          ""
                                        }
                                        onChange={e => {
                                          handleChangeScoreLineComponents({
                                            specialityId: item.specialityId,
                                            lineId: item.lineId,
                                            componentId: e.target.value,
                                          });
                                        }}
                                        onBlur={() =>
                                          setTouchedComponentIds(prev => new Set([...prev, item.specialityId]))
                                        }
                                        sx={{ height: "3rem" }}
                                      >
                                        <MenuItem value={""}></MenuItem>
                                        {item?.options?.map((option, i) => (
                                          <MenuItem
                                            key={"componentId-" + i}
                                            value={option.id}
                                            style={{}}
                                          >
                                            {option.nameBg}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {touchedComponentIds.has(item.specialityId) &&
                                        !components?.find(x => x.specialityId === item?.specialityId)?.componentId && (
                                          <FormHelperText>Задължително поле</FormHelperText>
                                        )}
                                    </FormControl>
                                  )
                                );
                              })}
                            </Grid>
                          </Grid>
                        );
                      }}
                    </FormDataConsumer>
                  </SimpleFormIterator>
                </ArrayInput>
              </Grid>
              {!isRestoring && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{ marginLeft: "1rem" }}
                >
                  <ReferenceInput
                    source="admissionAfterEducationLevelId"
                    reference={administrationRequests.getAdmissionAfterEducationLevelsSelect()}
                  >
                    <AutocompleteInput
                      optionText="name"
                      label="Прием след"
                      fullWidth
                    />
                  </ReferenceInput>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </>

      {!isForeigner &&
        availableExams?.map((exam, idx) => {
          return (
            <Grid
              item
              xs={12}
              md={4}
              key={idx}
            >
              <TextField
                label={`Оценка от диплома по ${exam.nameBg} `}
                InputProps={{
                  inputProps: {
                    max: 6,
                    min: 2,
                    step: 0.01,
                  },
                }}
                error={error}
                helperText={helperText}
                type="number"
                onChange={e => {
                  validate(e.target.value);
                  setSchoolSubjectGrades(oldValues => {
                    if (oldValues?.length > 0) {
                      const updatedValues = [
                        ...oldValues.filter(i => i.subjectId !== exam.id),
                        { subjectId: exam.id, grade: +e.target.value },
                      ];
                      return updatedValues;
                    } else {
                      const updatedValues = [{ subjectId: exam.id, grade: +e.target.value }];
                      return updatedValues;
                    }
                  });
                }}
              />
            </Grid>
          );
        })}

      {isForeigner && (
        <>
          <Grid
            item
            xs={12}
            md={4}
          >
            <NullableBooleanInput
              label="Ще ползва подготвителен клас"
              source="usingPreparatoryClass"
              nullLabel=""
              falseLabel="Не"
              trueLabel="Да"
              validate={required()}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <NullableBooleanInput
              label="Участва в класиране"
              source="participatesInRanking"
              nullLabel=""
              falseLabel="Не"
              trueLabel="Да"
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <ReferenceInput
              source="intermediaryId"
              reference={administrationRequests.getIntermediariesSelect()}
            >
              <SelectInput
                label="Представител"
                optionText="name"
              />
            </ReferenceInput>
          </Grid>
        </>
      )}

      {!isForeigner ? (
        <Grid
          item
          xs={12}
        >
          <BooleanInput
            label="Иска да възстанови студентските си права"
            source={"reinstatesRights"}
            sx={{ width: "fit-content" }}
          />
        </Grid>
      ) : null}
      {isForeigner ? (
        <FormDataConsumer>
          {({ scopedFormData, formData }) => {
            return (
              <>
                <Grid
                  item
                  xs={12}
                >
                  <BooleanInput
                    label="Редовна кандидатура"
                    source={"regular"}
                    sx={{ width: "max-content" }}
                  />
                </Grid>
                {!formData.regular && (
                  <>
                    <Grid
                      item
                      xs={12}
                    >
                      <BooleanInput
                        label="Иска да възстанови студентските си права"
                        source={"reinstatesRights"}
                        onChange={e => {
                          setValue("isFromAnotherUniversity", !e.target.checked);
                        }}
                        sx={{ width: "fit-content" }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <BooleanInput
                        label="Трансфер от друг университет"
                        source="isFromAnotherUniversity"
                        onChange={e => {
                          setValue("reinstatesRights", !e.target.checked);
                        }}
                      />
                    </Grid>
                  </>
                )}
              </>
            );
          }}
        </FormDataConsumer>
      ) : null}

      {!isForeigner && (
        <Grid
          item
          xs={12}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <NullableBooleanInput
              label="Кандидатства за платено обучение"
              source={"onlyPaidEducation"}
              nullLabel=" "
              falseLabel="Не"
              trueLabel="Да"
              validate={required()}
            />
          </Grid>
        </Grid>
      )}
      <Grid
        item
        xs={12}
      >
        <TextInput
          source="note"
          label="Бележка"
          sx={settings.style.textAria}
          variant="outlined"
          fullWidth
          multiline
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <SelectInput
          source="instructionLanguageId"
          label="Език на обучение"
          choices={LanguageOptions}
          validate={required()}
        />
      </Grid>
      {isForeigner ? (
        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            label="Платена такса за обработка на документи"
            source="isPaidDocumentProcessing"
            choices={AdminExamsPaymentMethodChoices}
            validate={required()}
          />
        </Grid>
      ) : null}
      <Grid
        item
        xs={12}
        md={4}
      >
        {!usingPreparatoryClass && (
          <SelectInput
            readOnly={isFreeExams}
            label="Платена такса за изпит"
            source="isPaidExams"
            choices={
              !isForeigner
                ? AdminExamsPaymentMethodChoices.filter(c => c.id !== AdminExamsPaymentMethod.NoPayment)
                : AdminExamsPaymentMethodChoices
            }
            validate={[required(), paymentValidation]}
          />
        )}
      </Grid>
    </Grid>
  );
};
export default CandidacyInfo;
